import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { COLORS } from '@cuemath/comps';

import { closeModal } from 'actions/modal';

const AMBER = '#f5a623';
const AMBER_SOFT = '#fff4e0';
const INK = '#1f2430';
const INK_SOFT = '#5a606e';
const LINE = '#ece7db';
const GREEN = '#1f9d57';
const GREEN_SOFT = '#e8f6ee';
const BLUE = COLORS.CTA_LINK || '#2f6bff';

const FIGMA = {
  subscription:
    'https://www.figma.com/design/c5ZHWg7J4pTJkKbe0sAEDZ/Signup-Flows?node-id=13060-22853',
  manage1:
    'https://www.figma.com/design/0LyCGkMddCnucpIM1wPDwv/CueParent-app?node-id=18399-84036',
  manage2:
    'https://www.figma.com/design/0LyCGkMddCnucpIM1wPDwv/CueParent-app?node-id=18397-77310',
};

const Root = styled.div`
  padding: 20px 24px 28px;
  color: ${INK};
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.55;
  overflow-y: auto;
`;
const Eyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 11px;
  font-weight: 800;
  color: ${AMBER};
  margin-bottom: 6px;
`;
const H1 = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
`;
const H2 = styled.div`
  font-size: 17px;
  font-weight: 800;
  margin-bottom: 4px;
`;
const P = styled.div`
  font-size: 14px;
  color: ${INK_SOFT};
`;
const IntroP = styled(P)`
  margin-top: 8px;
`;
const Section = styled.div`
  padding: 20px 0;
  border-top: 1px solid ${LINE};
  ${p => (p.$last ? 'border-bottom: 0;' : '')}
`;
const Cards = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
`;
const TogglesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
`;
const Caption = styled.div`
  font-size: 12px;
  font-style: italic;
  color: ${INK_SOFT};
  margin: 2px 0 4px;
`;
// Dimmed dot glyphs standing in for a real price — clearly not a live number.
const Ghost = styled.span`
  color: #c2c7d0;
  letter-spacing: 0.06em;
`;
const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border: 1px solid ${LINE};
  border-radius: 5px;
  padding: 1px 6px;
  word-break: break-all;
`;
const Card = styled.div`
  flex: 1 1 150px;
  min-width: 150px;
  border: 1px solid ${p => (p.$pop ? AMBER : LINE)};
  background: ${p => (p.$pop ? AMBER_SOFT : COLORS.WHITE)};
  border-radius: 10px;
  padding: 12px 13px;
`;
const CardTop = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${INK_SOFT};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;
const Price = styled.div`
  font-size: 24px;
  font-weight: 800;
`;
const MPrice = styled(Price)`
  font-size: 16px;
`;
const Small = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${INK_SOFT};
`;
const Meta = styled.div`
  font-size: 12.5px;
  color: ${INK_SOFT};
  margin-top: 5px;
`;
const Save = styled.div`
  color: ${GREEN};
  font-weight: 700;
  font-size: 12px;
  margin-top: 6px;
`;
const Note = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;
const NoteBox = styled.div`
  flex: 1 1 200px;
  border: 1px solid ${LINE};
  border-left: 4px solid ${p => p.$c || AMBER};
  border-radius: 8px;
  padding: 10px 13px;
  font-size: 13.5px;
`;
const NoteSub = styled.div`
  color: ${INK_SOFT};
  margin-top: 3px;
`;
const Tabs = styled.div`
  display: inline-flex;
  gap: 4px;
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border-radius: 999px;
  padding: 4px;
  margin: 14px 0 18px;
`;
const Tab = styled.button`
  border: 0;
  font: inherit;
  font-weight: 700;
  font-size: 13px;
  padding: 7px 15px;
  border-radius: 999px;
  cursor: pointer;
  background: ${p => (p.$on ? AMBER : 'transparent')};
  color: ${p => (p.$on ? '#3a2a05' : INK_SOFT)};
`;
const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px 18px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Step = styled.div`
  display: flex;
  gap: 12px;
`;
const Num = styled.div`
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${INK};
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 13px;
`;
const StepH = styled.div`
  font-weight: 800;
  font-size: 14.5px;
`;
const StepP = styled.div`
  font-size: 13px;
  color: ${INK_SOFT};
  margin: 2px 0 8px;
`;
const Phone = styled.div`
  max-width: 220px;
  border: 1px solid ${LINE};
  border-radius: 16px;
  overflow: hidden;
  background: ${COLORS.WHITE};
`;
const PBar = styled.div`
  height: 22px;
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border-bottom: 1px solid ${LINE};
`;
const PScr = styled.div`
  padding: 11px 12px 14px;
`;
const MTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
`;
const MSub = styled.div`
  font-size: 10.5px;
  color: ${INK_SOFT};
`;
const SchedLbl = styled(MSub)`
  font-weight: 800;
  color: ${GREEN};
`;
const MPlan = styled.div`
  border: 1px solid ${p => (p.$sel ? AMBER : LINE)};
  background: ${p => (p.$sel ? AMBER_SOFT : COLORS.WHITE)};
  border-radius: 8px;
  padding: 8px 9px;
  margin-top: 8px;
`;
const Sched = styled.div`
  background: ${GREEN_SOFT};
  border: 1px solid ${GREEN};
  border-radius: 8px;
  padding: 8px 9px;
  margin-top: 8px;
`;
const Row = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 8px;
`;
const Pay = styled.div`
  display: flex;
  justify-content: space-between;
  border: 1px solid ${LINE};
  border-radius: 7px;
  padding: 7px 9px;
  margin-top: 6px;
  font-size: 11.5px;
  font-weight: 600;
`;
const Cta = styled.div`
  margin-top: 10px;
  background: ${AMBER};
  color: #3a2a05;
  text-align: center;
  font-weight: 800;
  border-radius: 8px;
  padding: 8px;
  font-size: 12px;
`;
const Chip = styled.span`
  border: 1px solid ${LINE};
  border-radius: 7px;
  padding: 4px 9px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => (p.$on ? INK : COLORS.WHITE)};
  color: ${p => (p.$on ? '#fff' : INK)};
`;
const Success = styled.div`
  text-align: center;
  padding: 14px 4px;
`;
const Big = styled.div`
  font-size: 30px;
`;
const A = styled.a`
  color: ${BLUE};
  font-weight: 600;
  display: block;
  margin-top: 8px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 18px;
`;
const CloseBtn = styled.button`
  border: 1px solid ${LINE};
  background: ${COLORS.WHITE};
  border-radius: 8px;
  padding: 8px 18px;
  font-weight: 700;
  cursor: pointer;
`;

// Illustrative only — money is deliberately blanked out (the live panel above
// carries this student's real prices). We keep the tenure + real session counts
// so the sessions/week toggle stays meaningful; the market toggle just flips the
// currency symbol. Per-session price is the same across frequencies; only the
// session count (and therefore the total) differs.
const CUR = { india: '₹', intl: '$' };
const PANEL_CARDS = {
  '2': [
    { top: '3 months', sessions: '26 sessions' },
    { top: '6 months', sessions: '52 sessions', save: true },
    { top: '12 months', sessions: '104 sessions', save: true },
  ],
  '3': [
    { top: '3 months', sessions: '39 sessions' },
    { top: '6 months', sessions: '78 sessions', save: true },
    { top: '12 months', sessions: '156 sessions', save: true },
  ],
};

function PhoneFrame({ children }) {
  return (
    <Phone>
      <PBar />
      <PScr>{children}</PScr>
    </Phone>
  );
}

const INDIA_STEPS = [
  {
    h: 'Trial done → pick a plan',
    p: 'After the trial, the parent picks a plan — the same cards you see in the panel.',
    scr: (
      <>
        <MTitle>Trial class completed! 🎉</MTitle>
        <MSub>Pick a plan and enrol</MSub>
        <MPlan>
          <MPrice>
            ₹<Ghost>•••</Ghost> <Small>/session</Small>
          </MPrice>
          <MSub>3 months · 26 sessions</MSub>
        </MPlan>
        <MPlan $sel>
          <MPrice>
            ₹<Ghost>•••</Ghost> <Small>/session</Small>
          </MPrice>
          <MSub>12 months · 104 sessions</MSub>
        </MPlan>
      </>
    ),
  },
  {
    h: 'Confirm the class schedule',
    p: 'Their weekly slots show up top (you can pre-set these). Confirmed on enrol.',
    scr: (
      <Sched>
        <SchedLbl>PREFERRED SCHEDULE ✎</SchedLbl>
        <MTitle>Tue 8:30 PM + Sat 6:00 PM</MTitle>
        <MSub>First class — Wed, Jul 15</MSub>
      </Sched>
    ),
  },
  {
    h: 'Pay — UPI, cards or net-banking',
    p:
      'One upfront payment via Razorpay / Paytm. Card EMI is hidden by default — switch it on via an override if the parent needs it.',
    scr: (
      <>
        <MTitle>
          You pay today · ₹<Ghost>••,•••</Ghost>
        </MTitle>
        <Pay>
          <span>💳 Cards</span>
          <span>›</span>
        </Pay>
        <Pay>
          <span>UPI</span>
          <span>›</span>
        </Pay>
        <Pay>
          <span>🏦 Net banking</span>
          <span>›</span>
        </Pay>
        <Cta>Pay now</Cta>
      </>
    ),
  },
  {
    h: 'Enrolled 🎉',
    p: 'Held slots become real classes and the learning home opens.',
    scr: (
      <Success>
        <Big>✅</Big>
        <MTitle>You're enrolled!</MTitle>
        <MSub>Classes start Wed, Jul 15</MSub>
      </Success>
    ),
  },
];

const INTL_STEPS = [
  {
    h: 'Pick a plan',
    p: 'Same cards in local currency. These are subscriptions (billed each cycle).',
    scr: (
      <>
        <MTitle>Pick a plan and enrol</MTitle>
        <MSub>Every session is 60 mins</MSub>
        <MPlan $sel>
          <MPrice>
            $<Ghost>••</Ghost> <Small>/session</Small>
          </MPrice>
          <MSub>Annual · 104 sessions</MSub>
        </MPlan>
        <MPlan>
          <MPrice>
            $<Ghost>••</Ghost> <Small>/session</Small>
          </MPrice>
          <MSub>Quarterly · 26 sessions</MSub>
        </MPlan>
      </>
    ),
  },
  {
    h: 'Choose class times',
    p: "Pick a day and time for each weekly session, from the tutor's free slots.",
    scr: (
      <>
        <MTitle>Session 1 of 2</MTitle>
        <MSub>Mon–Sun · morning / afternoon / evening</MSub>
        <Row>
          <Chip>Mon</Chip>
          <Chip $on>Tue</Chip>
          <Chip>Thu</Chip>
        </Row>
        <Row>
          <Chip>4 PM</Chip>
          <Chip $on>5 PM</Chip>
          <Chip>6 PM</Chip>
        </Row>
      </>
    ),
  },
  {
    h: 'Pay by card',
    p:
      'Card payment defaults to XPay (Stripe can be switched on via an override) — this starts the recurring subscription.',
    scr: (
      <>
        <MTitle>Semi-annual plan</MTitle>
        <MSub>52 sessions · 2/week</MSub>
        <Pay>
          <span>💳 Card number</span>
          <span>XPay</span>
        </Pay>
        <Pay>
          <span>Name &amp; billing address</span>
          <span>›</span>
        </Pay>
        <Cta>Pay &amp; subscribe</Cta>
      </>
    ),
  },
  {
    h: 'Subscription active 🎉',
    p: 'Classes are scheduled. Manage or pause later from the Manage-subscription page.',
    scr: (
      <Success>
        <Big>✅</Big>
        <MTitle>Subscription active</MTitle>
        <MSub>Manage or pause anytime</MSub>
      </Success>
    ),
  },
];

function PaymentFlowGuideModal() {
  const dispatch = useDispatch();
  const [flow, setFlow] = useState('india');
  const [cpw, setCpw] = useState('2');
  const steps = flow === 'india' ? INDIA_STEPS : INTL_STEPS;
  const sym = CUR[flow];

  return (
    <Root>
      <Eyebrow>🐻 Cuemath · Admissions payments</Eyebrow>
      <H1>How parents pick a plan and pay — on the leap platform</H1>
      <IntroP>
        Online admissions pay on the leap platform, not through a payment link — the
        Payment Setup panel shows the exact plans a parent sees. (Offline courses aren't
        on leap yet, so for those you still generate a one-time payment link.) Here's the
        online experience.
      </IntroP>

      <Section>
        <Eyebrow>What the panel shows you</Eyebrow>
        <H2>The same plans the parent sees</H2>
        <P>
          Each card is one plan. Price is shown per session, with the total below. Use the
          toggles to switch market and sessions/week:
        </P>
        <TogglesRow>
          <Tabs>
            <Tab $on={flow === 'india'} onClick={() => setFlow('india')}>
              🇮🇳 India · one-time
            </Tab>
            <Tab $on={flow === 'intl'} onClick={() => setFlow('intl')}>
              🌏 International · subscription
            </Tab>
          </Tabs>
          {/* Sessions/week toggle — the parent picks their frequency and the
              plan cards switch to match. */}
          <Tabs>
            <Tab $on={cpw === '2'} onClick={() => setCpw('2')}>
              2 sessions / week
            </Tab>
            <Tab $on={cpw === '3'} onClick={() => setCpw('3')}>
              3 sessions / week
            </Tab>
          </Tabs>
        </TogglesRow>
        <Caption>
          Example layout — the panel above shows this student's real prices.
        </Caption>
        <Cards>
          {PANEL_CARDS[cpw].map(c => (
            <Card key={c.top}>
              <CardTop>{c.top}</CardTop>
              <Price>
                {sym}
                <Ghost>•••</Ghost> <Small>/session</Small>
              </Price>
              <Meta>
                {c.sessions}
                <br />
                {sym}
                <Ghost>••••</Ghost> total
              </Meta>
              {c.save ? (
                <Save>
                  Save <Ghost>••</Ghost>%
                </Save>
              ) : null}
            </Card>
          ))}
        </Cards>
        <Note>
          <NoteBox $c={GREEN}>
            <b>Sessions / week</b>
            <NoteSub>
              The parent picks <b>2/week</b> or <b>3/week</b> (toggle above). Same
              per-session price; more sessions means a higher total.
            </NoteSub>
          </NoteBox>
          <NoteBox>
            <b>Enrollment type</b>
            <NoteSub>
              India shows <b>IBG</b>. International shows <b>PLUS</b>.
            </NoteSub>
          </NoteBox>
          <NoteBox $c={BLUE}>
            <b>Payment type</b>
            <NoteSub>
              India pays once (one-time). International is a subscription.
            </NoteSub>
          </NoteBox>
        </Note>
      </Section>

      <Section>
        <Eyebrow>Step by step</Eyebrow>
        <H2>What the parent actually does</H2>
        <Tabs>
          <Tab $on={flow === 'india'} onClick={() => setFlow('india')}>
            🇮🇳 India · one-time
          </Tab>
          <Tab $on={flow === 'intl'} onClick={() => setFlow('intl')}>
            🌏 International · subscription
          </Tab>
        </Tabs>
        <Steps>
          {steps.map((s, i) => (
            <Step key={s.h}>
              <Num>{i + 1}</Num>
              <div>
                <StepH>{s.h}</StepH>
                <StepP>{s.p}</StepP>
                <PhoneFrame>{s.scr}</PhoneFrame>
              </div>
            </Step>
          ))}
        </Steps>
      </Section>

      <Section>
        <Eyebrow>Why it changed</Eyebrow>
        <H2>Why subscription-link generation is off for admissions</H2>
        <P>
          Online payments moved to the leap platform so plans, schedule and payment are
          one flow — so subscription-link generation is off for admissions and everyone
          uses the same verified path. Offline courses aren't on leap yet, so for those
          you still generate a one-time payment link (restricted to offline plans).
        </P>
      </Section>

      <Section>
        <Eyebrow>Website payment link</Eyebrow>
        <H2>If a parent opens a cuemath.com payment link</H2>
        <P>
          If an admissions parent lands on a website payment URL like{' '}
          <Mono>https://www.cuemath.com/parent/leap-payment/XYZ/</Mono>, they're
          redirected to the leap platform, where they continue to pay through the same
          flow shown above.
        </P>
      </Section>

      <Section>
        <Eyebrow>Need a change?</Eyebrow>
        <H2>Who to contact for overrides</H2>
        <P>
          Changing what a parent sees (enrollment type, assigned teacher, or preferred
          slots) is limited to an approved allow-list. If you need a change and don't have
          access, reach out to your team lead.
        </P>
      </Section>

      <Section $last>
        <Eyebrow>The real designs</Eyebrow>
        <H2>See the actual leap screens</H2>
        <A href={FIGMA.subscription} target="_blank" rel="noreferrer">
          → Subscription (international) post-trial flow — Figma
        </A>
        <A href={FIGMA.manage1} target="_blank" rel="noreferrer">
          → Manage subscription (active / grace period) — Figma
        </A>
        <A href={FIGMA.manage2} target="_blank" rel="noreferrer">
          → Manage subscription — alternate state — Figma
        </A>
      </Section>

      <Footer>
        <CloseBtn onClick={() => dispatch(closeModal())}>Close</CloseBtn>
      </Footer>
    </Root>
  );
}

export default PaymentFlowGuideModal;
